import { formatMoney, formatViews } from '../core/math';
import { TICKER_FILLER } from '../game/director';
import type { Game } from '../game/Game';
import type { GameEvent, SpeakerId } from '../game/types';
import { dottieSprite } from '../render/characters';
import { SCENE_W, headAnchor } from '../render/layout';
import type { SceneRenderer } from '../render/scene';
import { paintSpriteInto } from '../render/sprite';
import { h, setText, toggleClass } from './dom';

interface Bubble {
  el: HTMLElement;
  until: number;
  speaker: SpeakerId;
}

/**
 * Everything drawn in HTML on top of the pixel scene: speech bubbles, big
 * banners, the fighting-game HUD, floating tips, the news chyron and Dottie.
 */
export class SceneOverlay {
  readonly el: HTMLElement;
  private readonly bubbles = new Map<string, Bubble>();
  private readonly bubbleLayer: HTMLElement;
  private readonly floaters: HTMLElement;
  private readonly banner: HTMLElement;
  private readonly bannerText: HTMLElement;
  private readonly bannerSub: HTMLElement;
  private bannerUntil = 0;
  private readonly fightHud: HTMLElement;
  private readonly fightNames: [HTMLElement, HTMLElement];
  private readonly fightBars: [HTMLElement, HTMLElement];
  private readonly topic: HTMLElement;
  private readonly live: HTMLElement;
  private readonly status: HTMLElement;
  private readonly catchPrompt: HTMLElement;
  private readonly crowd: HTMLElement;
  private crowdUntil = 0;
  private readonly dottie: HTMLElement;
  private readonly dottieLine: HTMLElement;
  private readonly dottieFace: HTMLCanvasElement;
  private dottieUntil = 0;
  private readonly tickerLabel: HTMLElement;
  private readonly tickerText: HTMLElement;
  private readonly headlines: { text: string; breaking: boolean }[] = [];
  private fillerIndex = Math.floor(Math.random() * TICKER_FILLER.length);
  private chairText = '';
  private chairTextUntil = 0;
  private now = 0;

  constructor(
    private readonly renderer: SceneRenderer,
    private readonly keyHints: () => boolean,
  ) {
    this.bubbleLayer = h('div', { class: 'overlay__bubbles', 'aria-hidden': 'true' });
    this.floaters = h('div', { class: 'overlay__floaters', 'aria-hidden': 'true' });
    this.bannerText = h('div', { class: 'banner__text' });
    this.bannerSub = h('div', { class: 'banner__sub' });
    this.banner = h('div', { class: 'banner', role: 'status' }, this.bannerText, this.bannerSub);

    const nameA = h('span', { class: 'fight__name' });
    const nameB = h('span', { class: 'fight__name fight__name--b' });
    const barA = h('div', { class: 'fight__fill' });
    const barB = h('div', { class: 'fight__fill' });
    this.fightNames = [nameA, nameB];
    this.fightBars = [barA, barB];
    this.topic = h('div', { class: 'fight__topic' });
    this.fightHud = h(
      'div',
      { class: 'fight', 'aria-hidden': 'true' },
      h('div', { class: 'fight__side' }, nameA, h('div', { class: 'fight__bar' }, barA)),
      h('div', { class: 'fight__vs' }, h('span', { class: 'fight__timer' }, '∞'), 'VS'),
      h(
        'div',
        { class: 'fight__side fight__side--b' },
        nameB,
        h('div', { class: 'fight__bar fight__bar--b' }, barB),
      ),
      this.topic,
    );
    this.live = h('div', { class: 'live' });
    this.status = h('div', { class: 'scene-status' });
    this.catchPrompt = h('div', { class: 'catch', 'aria-live': 'assertive' });
    this.crowd = h('div', { class: 'crowd' });

    this.dottieFace = h('canvas', { class: 'dottie__face', width: 20, height: 30 });
    paintSpriteInto(this.dottieFace, dottieSprite('talk'), 2);
    this.dottieLine = h('div', { class: 'dottie__line' });
    this.dottie = h(
      'div',
      { class: 'dottie' },
      this.dottieFace,
      h('div', { class: 'dottie__bubble' }, h('b', null, 'DOTTIE'), this.dottieLine),
    );

    this.tickerLabel = h('span', { class: 'ticker__label' }, 'WHN');
    this.tickerText = h('span', { class: 'ticker__text' });
    this.tickerText.addEventListener('animationend', () => this.nextHeadline());
    const ticker = h(
      'div',
      { class: 'ticker', 'aria-hidden': 'true' },
      this.tickerLabel,
      h('div', { class: 'ticker__track' }, this.tickerText),
    );

    this.el = h(
      'div',
      { class: 'overlay' },
      this.bubbleLayer,
      this.floaters,
      this.fightHud,
      this.status,
      this.live,
      this.crowd,
      this.banner,
      this.catchPrompt,
      this.dottie,
      ticker,
    );
    this.nextHeadline();
  }

  reset(): void {
    for (const b of this.bubbles.values()) b.el.remove();
    this.bubbles.clear();
    this.floaters.replaceChildren();
    this.bannerUntil = 0;
    this.dottieUntil = 0;
    this.crowdUntil = 0;
    this.chairTextUntil = 0;
    this.headlines.length = 0;
    toggleClass(this.banner, 'is-on', false);
  }

  private get scale(): number {
    return this.el.clientWidth / SCENE_W;
  }

  handle(e: GameEvent): void {
    switch (e.type) {
      case 'speech':
        this.speak(e.speaker, e.text, e.duration, e.mood ?? 'normal');
        break;
      case 'banner':
        this.showBanner(e.text, e.sub ?? '', e.style, e.duration);
        break;
      case 'headline':
        if (e.breaking) this.headlines.unshift({ text: e.text, breaking: true });
        else this.headlines.push({ text: e.text, breaking: false });
        if (this.headlines.length > 6) this.headlines.length = 6;
        break;
      case 'tip':
        this.floatText(e.seatId, `+${formatMoney(e.amount)}`, e.label);
        break;
      case 'chairResolved': {
        const text =
          e.outcome === 'perfect'
            ? 'THERE IS NO CHAIR.'
            : e.outcome === 'caught'
              ? 'CAUGHT IT!'
              : e.outcome === 'whiff'
                ? 'WHIFFED!'
                : 'CRASH!';
        this.chairText = e.collateral ? `${text}\n${e.collateral.replace(/^CRASH! /, '')}` : text;
        this.chairTextUntil = this.now + 1.6;
        break;
      }
      case 'ticketServed':
        if (e.combo >= 3 && e.perfect)
          this.floatText(e.ticket.seatId, `COMBO ×${e.combo}`, 'combo');
        if (e.multiplier > 1)
          this.floatText(e.ticket.seatId, `×${e.multiplier} TIPS`, 'unbothered');
        break;
      default:
        break;
    }
  }

  private speak(speaker: SpeakerId, text: string, duration: number, mood: string): void {
    if (speaker === 'dottie') {
      setText(this.dottieLine, text);
      this.dottieUntil = this.now + duration + 0.6;
      toggleClass(this.dottie, 'is-on', true);
      return;
    }
    if (speaker === 'crowd') {
      setText(this.crowd, text);
      this.crowdUntil = this.now + duration;
      toggleClass(this.crowd, 'is-on', true);
      return;
    }
    const key = String(speaker);
    let bubble = this.bubbles.get(key);
    if (!bubble) {
      if (this.bubbles.size >= 4) {
        const oldest = [...this.bubbles.entries()].sort((a, b) => a[1].until - b[1].until)[0];
        if (oldest) {
          oldest[1].el.remove();
          this.bubbles.delete(oldest[0]);
        }
      }
      bubble = { el: h('div', { class: 'bubble' }), until: 0, speaker };
      this.bubbleLayer.append(bubble.el);
      this.bubbles.set(key, bubble);
    }
    bubble.el.textContent = text;
    bubble.el.dataset.mood = mood;
    bubble.until = this.now + duration + 0.4;
  }

  private showBanner(text: string, sub: string, style: string, duration: number): void {
    setText(this.bannerText, text);
    setText(this.bannerSub, sub);
    this.banner.dataset.style = style;
    this.bannerUntil = this.now + duration;
    this.banner.classList.remove('is-on');
    void this.banner.offsetWidth;
    this.banner.classList.add('is-on');
  }

  private floatText(seatId: number, text: string, kind: string): void {
    const p = headAnchor(seatId);
    const s = this.scale;
    const el = h(
      'div',
      { class: `floater floater--${kind}`, style: `left:${p.x * s}px;top:${(p.y + 8) * s}px` },
      text,
    );
    el.addEventListener('animationend', () => el.remove());
    this.floaters.append(el);
    if (this.floaters.childElementCount > 12) this.floaters.firstElementChild?.remove();
  }

  private nextHeadline(): void {
    const next = this.headlines.shift() ?? {
      text: TICKER_FILLER[this.fillerIndex++ % TICKER_FILLER.length] ?? '',
      breaking: false,
    };
    setText(this.tickerLabel, next.breaking ? 'BREAKING' : 'WHN 3AM');
    toggleClass(this.tickerLabel, 'is-breaking', next.breaking);
    this.tickerText.textContent = next.text;
    const seconds = Math.max(9, next.text.length * 0.18);
    this.tickerText.style.animation = 'none';
    void this.tickerText.offsetWidth;
    this.tickerText.style.animation = `ticker ${seconds}s linear 1`;
  }

  update(game: Game, dt: number): void {
    this.now += dt;
    const s = this.scale;

    // Speech bubbles follow their speakers.
    for (const [key, b] of this.bubbles) {
      const anchor = this.renderer.speakerAnchor(game, b.speaker);
      if (!anchor || this.now > b.until) {
        b.el.remove();
        this.bubbles.delete(key);
        continue;
      }
      const width = b.el.offsetWidth;
      const maxX = this.el.clientWidth - width / 2 - 4;
      const x = Math.min(maxX, Math.max(width / 2 + 4, anchor.x * s));
      const tail = anchor.x * s - x;
      b.el.style.left = `${x}px`;
      b.el.style.top = `${anchor.y * s}px`;
      b.el.style.setProperty('--tail', `${tail}px`);
    }

    if (this.now > this.bannerUntil) toggleClass(this.banner, 'is-on', false);
    if (this.now > this.dottieUntil) toggleClass(this.dottie, 'is-on', false);
    if (this.now > this.crowdUntil) toggleClass(this.crowd, 'is-on', false);

    // Fighting-game HUD
    const f = game.fight;
    const showFight = f !== null && f.phase !== 'argue';
    toggleClass(this.fightHud, 'is-on', showFight);
    if (f) {
      setText(this.fightNames[0], f.fighters[0]);
      setText(this.fightNames[1], f.fighters[1]);
      this.fightBars[0].style.transform = `scaleX(${f.hp[0]})`;
      this.fightBars[1].style.transform = `scaleX(${f.hp[1]})`;
      setText(this.topic, `RE: ${f.topic}`);
    }

    // Live stream badge
    toggleClass(this.live, 'is-on', game.live !== null);
    if (game.live) setText(this.live, `● LIVE  ${formatViews(game.live.viewers)} watching`);

    // Storm / power status
    const statusText = !game.powerOn
      ? '⚡ POWER OUT — WAFFLE IRONS DOWN'
      : game.storm
        ? `🌀 HURRICANE ${game.storm.name} · FEMA IS WATCHING`
        : '';
    setText(this.status, statusText);
    toggleClass(this.status, 'is-on', statusText !== '');

    // Chair QTE prompt
    const ch = game.chair;
    if (ch && !ch.outcome) {
      const inWindow = ch.t >= ch.windowStart;
      setText(
        this.catchPrompt,
        this.keyHints()
          ? inWindow
            ? 'NOW! [SPACE]'
            : 'CHAIR! GET READY… [SPACE]'
          : inWindow
            ? 'TAP NOW!'
            : 'CHAIR! TAP TO CATCH…',
      );
      this.catchPrompt.dataset.state = inWindow ? 'now' : 'ready';
      toggleClass(this.catchPrompt, 'is-on', true);
    } else if (this.now < this.chairTextUntil) {
      setText(this.catchPrompt, this.chairText);
      this.catchPrompt.dataset.state =
        this.chairText.startsWith('CAUGHT') || this.chairText.startsWith('THERE')
          ? 'caught'
          : 'miss';
      toggleClass(this.catchPrompt, 'is-on', true);
    } else {
      toggleClass(this.catchPrompt, 'is-on', false);
    }
  }
}
